import React, {useCallback, useContext} from 'react';
import { Platform, View, Text, Image, StyleSheet, NativeSyntheticEvent, NativeScrollEvent, Dimensions, Animated, ScrollView, TouchableOpacity, StatusBar } from 'react-native';
import { fetchAuthSession } from 'aws-amplify/auth';
import { apiUrl } from './utils/config';
import { useNavigation, NavigationProp } from '@react-navigation/native'; 
import { RootStackParamList } from './App.web';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import { LoadingContext } from './LoadingContext';
import ParentalGateModal from './ParentalGateModal';

// Hide horizontal scrollbar for .no-horizontal-scrollbar (web only)
if (typeof document !== "undefined" && Platform.OS === "web") {
  const style = document.createElement("style");
  style.innerHTML = `
    html, body {
      overflow-x: hidden !important;
    }
    .no-horizontal-scrollbar {
      overflow-x: hidden !important;
      scrollbar-width: none !important; /* Firefox */
      -ms-overflow-style: none !important; /* IE 10+ */
    }
    .no-horizontal-scrollbar::-webkit-scrollbar {
      display: none !important;
      height: 0 !important;
    }
  `;
  document.head.appendChild(style);
}


const { width, height } = Dimensions.get('window');
const guidelineBaseWidth = 390;  // iPhone 13 mini width
const guidelineBaseHeight = 844; // iPhone 13 mini height

const isMobile = Platform.OS !== 'web' && width < 700; // heuristic for small screens

const scale = (size: number) => isMobile ? width / guidelineBaseWidth * size : size;
const verticalScale = (size: number) => isMobile ? height / guidelineBaseHeight * size : size;
const moderateScale = (size: number, factor = 0.5) =>
  size + (scale(size) - size) * factor;
const DOT_SIZE = 10;
const DASH_WIDTH = 34;
const DOT_SPACING = 16;

const imageAssets: { [key: string]: any } = {
  storyBrowse: require('./assets/storybrowse.png'),
  imagesOrNo: require('./assets/imagesorno.png'),
  characterCr: require('./assets/charactercr.png'),
  characterImg: require('./assets/characterimg.png'),
  possibilities: require('./assets/possibilities.png'),
  readingTogether: require('./assets/readingtogether.png'),
  adFree: require('./assets/adfree.png'),
  logo: require('./assets/logo.png'),
  coins: require('./assets/coins.gif'),
  underseaThumbnail: require('./assets/undersea-thumbnail.png'),
  questThumbnail: require('./assets/quest-thumbnail.png'),
  volcanoThumbnail: require('./assets/volcano-thumbnail.png'),
  caveThumbnail: require('./assets/cave-thumbnail.png'),
  choose: require('./assets/choose.png'),
};

type SlideSection = {
  subtitle: string;
  text: string;
  image: string; // this is the path to the image, e.g. './assets/choose.jpeg'
};

type FeatureSlide = {
  title: string;
  top: SlideSection;
  bottom: SlideSection;
};
type IntroSlide = { isIntro: true };
type PlanSlide = { isPlan: true };

type Slide = FeatureSlide | IntroSlide | PlanSlide;

type AnimatedSplitSlideProps = {
  title: string;
  top: SlideSection;
  bottom: SlideSection;
  isActive: boolean;
};

type PlanSelectorHubProps = {
  onTryFree: () => void;
  onViewSubscriptions: () => void;
};

type FreeTokensSlideProps = {
  onChooseStory: () => void;
  onBack: () => void;
};

function isPlanSlide(slide: Slide): slide is PlanSlide {
  return "isPlan" in slide && slide.isPlan === true;
}
function isFeatureSlide(slide: Slide): slide is FeatureSlide {
  return "title" in slide;
}


const slides: Slide[] = [
  { isIntro: true },
  {
    title: "Choose Your Story",
    top: {
      subtitle: "Browse Magical Adventures",
      text: "Pick from a library of enchanting worlds—every story is new.",
      image: 'storyBrowse',
    },
    bottom: {
      subtitle: "Images or Not—You Decide",
      text: "Pick stories with magical illustrations, or choose text-only for pure imagination. It’s bedtime your way!",
      image: 'imagesOrNo',
    },
  },
  {
    title: "Create Your Character",
    top: {
      subtitle: "Name Your Hero",
      text: "Pick a name to make the story uniquely yours.",
      image: 'characterCr',
    },
    bottom: {
      subtitle: "Personalize Your Hero",
      text: "Choose style, traits, colors, and accessories for your character.",
      image: 'characterImg',
    },
  },
  {
    title: "Make a Decision, Shape the Story",
    top: {
      subtitle: "Your Choices Matter",
      text: "Decide what happens next—every choice creates a new path for your adventure!",
      image: 'choose',
    },
    bottom: {
      subtitle: "Endless Possibilities",
      text: "No two nights are ever the same. Try new choices to discover hidden endings and surprises!",
      image: 'possibilities',
    },
  },
  {
    title: "Enjoy Bedtime—Together!",
    top: {
      subtitle: "Special Moments Every Night",
      text: "Create memories as you read, choose, and laugh together—bedtime will become everyone’s favorite part of the day!",
      image: 'readingTogether',
    },
    bottom: {
      subtitle: "Safe & Positive",
      text: "Stories are age-appropriate, gentle, and ad-free—so you can relax and enjoy.",
      image: 'adFree',
    },
  },
  { isPlan: true }, // PlanSelectorHub slide
];

const NUM_SLIDES = slides.length;
const DOT_TRACK_WIDTH = (NUM_SLIDES - 1) * DOT_SPACING + NUM_SLIDES * DOT_SIZE + (DASH_WIDTH - DOT_SIZE);

const BTN_WIDTH = 115;
const BTN_WIDE_WIDTH = 185;

function IntroSlide() {
  return (
    <View style={styles.introSlide}>
      <Image source={imageAssets['underseaThumbnail']} style={[styles.pairedImg, isMobile ? { position: 'absolute', top: verticalScale(45), left: scale(6), width: scale(135), height: verticalScale(135) } : { position: 'absolute', top: 100, left: 30, width: 180, height: 180 }]} />
      <Image source={imageAssets['questThumbnail']} style={[styles.pairedImg, isMobile ? { position: 'absolute', top: verticalScale(90), right: scale(-8), width: scale(90), height: verticalScale(90) } : { position: 'absolute', top: 120, right: -20, width: 110, height: 110 }]} />
      <Image source={imageAssets['volcanoThumbnail']} style={[styles.pairedImg, isMobile ? { position: 'absolute', bottom: verticalScale(90), left: scale(18), width: scale(90), height: verticalScale(90) } : { position: 'absolute', bottom: 120, left: 55, width: 110, height: 110 }]} />
      <Image source={imageAssets['caveThumbnail']} style={[styles.pairedImg, isMobile ? { position: 'absolute', bottom: verticalScale(35), right: scale(-8), width: scale(135), height: verticalScale(135) } : { position: 'absolute', bottom: 60, right: -20, width: 180, height: 180 }]} />
      <View style={styles.centerTextBox}>
        <Text style={styles.welcomeSmall}>Welcome to Interactive Bedtime</Text>
        <Text style={styles.welcomeLarge}>Where Every{'\n'}Bedtime Becomes {'\n'}an Adventure.</Text>
      </View>
    </View>
  );
}

function AnimatedSplitSlide({ title, top, bottom, isActive }: AnimatedSplitSlideProps) {

  const titleY = React.useRef(new Animated.Value(-50)).current;
  const topTextX = React.useRef(new Animated.Value(-width)).current;
  const topImgX = React.useRef(new Animated.Value(width)).current;
  const bottomImgX = React.useRef(new Animated.Value(-width)).current;
  const bottomTextX = React.useRef(new Animated.Value(width)).current;

  React.useEffect(() => {
    if (isActive) {
      Animated.stagger(140, [
        Animated.timing(titleY, { toValue: 0, duration: 650, useNativeDriver: true }),
        Animated.parallel([
          Animated.timing(topTextX, { toValue: 0, duration: 650, useNativeDriver: true }),
          Animated.timing(topImgX, { toValue: 0, duration: 650, useNativeDriver: true }),
        ]),
        Animated.parallel([
          Animated.timing(bottomImgX, { toValue: 0, duration: 650, useNativeDriver: true }),
          Animated.timing(bottomTextX, { toValue: 0, duration: 650, useNativeDriver: true }),
        ]),
      ]).start();
    } else {
      titleY.setValue(-50);
      topTextX.setValue(-width);
      topImgX.setValue(width);
      bottomImgX.setValue(-width);
      bottomTextX.setValue(width);
    }
  }, [isActive]);

  return (
    <View style={styles.splitSlide}>
      <Animated.Text
        style={[
          styles.splitTitle,
          {
            position: 'absolute',
            top: verticalScale(100), // Or whatever spacing you want
            left: 0,
            right: 0,
            transform: [{ translateY: titleY }],
            zIndex: 1,
          }
        ]}
      >
        {title}
      </Animated.Text>
      <View style={styles.splitRowTop}>
        <Animated.View style={{ flex: 1, marginRight: 10, transform: [{ translateX: topTextX }] }}>
          <View style={styles.splitTextBox}>
            <Text style={styles.splitSubtitle}>{top.subtitle}</Text>
            <Text style={styles.splitBody}>{top.text}</Text>
          </View>
        </Animated.View>
        <Animated.Image source={imageAssets[top.image]} style={[styles.splitImg, { transform: [{ translateX: topImgX }] }]} />
      </View>
      <View style={styles.splitRowBottom}>
        <Animated.Image source={imageAssets[bottom.image]} style={[styles.splitImg, { transform: [{ translateX: bottomImgX }] }]} />
        <Animated.View style={{ flex: 1, marginLeft: 10, transform: [{ translateX: bottomTextX }] }}>
          <View style={styles.splitTextBox}>
            <Text style={styles.splitSubtitle}>{bottom.subtitle}</Text>
            <Text style={styles.splitBody}>{bottom.text}</Text>
          </View>
        </Animated.View>
      </View>
    </View>
  );
}

function PlanSelectorHub({ onTryFree, onViewSubscriptions }: PlanSelectorHubProps) {
  return (
    <View style={styles.planSlide}>
      <Image source={require('./assets/logo.png')} style={styles.planHeroImg} />
      <Text style={styles.planTitle}>How would you like to get started?</Text>
      <Text style={styles.planBullet}>Try your first bedtime story for free, or see all subscription options below.</Text>

      <TouchableOpacity style={styles.bigPrimaryBtn} onPress={onTryFree}>
        <Text style={styles.bigPrimaryBtnText}>Try Your First Story Free</Text>
        <Text style={styles.planFreeDesc}>
          We’ll give you 2 free tokens—no commitment, no credit card required.
        </Text>
      </TouchableOpacity>

      <TouchableOpacity style={styles.bigSecondaryBtn} onPress={onViewSubscriptions}>
        <Text style={styles.bigSecondaryBtnText}>View Subscription Options</Text>
        <Text style={styles.planSubDesc}>
          See all plans and features for unlimited bedtime adventures.
        </Text>
      </TouchableOpacity>
    </View>
  );
}

function FreeTokensSlide({ onChooseStory, onBack }: FreeTokensSlideProps) {
  const bounceAnim = React.useRef(new Animated.Value(1)).current;

  React.useEffect(() => {
    Animated.loop(
      Animated.sequence([
        Animated.timing(bounceAnim, { toValue: 1.15, duration: 500, useNativeDriver: true }),
        Animated.timing(bounceAnim, { toValue: 1, duration: 500, useNativeDriver: true }),
      ]),
    ).start();
  }, []);

  return (
    <View style={styles.tokensSlide}>
      <Animated.Image
        source={imageAssets['coins']}
        style={[styles.tokensImg, { transform: [{ scale: bounceAnim }] }]}
        resizeMode="contain"
      />
      <Text style={styles.tokensTitle}>You’ve got 2 free tokens!</Text>
      <Text style={styles.tokensSubtitle}>
        Use your tokens to enjoy any 7-chapter story with images.{"\n"}
        <Text style={{ fontStyle: 'italic', fontSize: 14 }}>
          Longer stories cost more tokens, but you can always upgrade later!
        </Text>
      </Text>

      <TouchableOpacity style={styles.tokensCtaBtn} onPress={onChooseStory}>
        <Text style={styles.tokensCtaText}>Choose a Story</Text>
      </TouchableOpacity>
      <TouchableOpacity onPress={onBack} style={{ marginTop: 20 }}>
        <Text style={{ color: '#544564', textDecorationLine: 'underline', fontSize: 15 }}>Back</Text>
      </TouchableOpacity>
    </View>
  );
}

export default function App() {
  const scrollX = React.useRef(new Animated.Value(0)).current;
  const scrollRef = React.useRef<ScrollView | null>(null);
  const [currentIndex, setCurrentIndex] = React.useState(0);
  const [showFreeTokens, setShowFreeTokens] = React.useState(false);
  const [showParentalGate, setShowParentalGate] = React.useState(false);
  const [gateIntent, setGateIntent] = React.useState<'subscription' | null>(null);
  const { loading, setLoading } = useContext(LoadingContext);

  // Animated width for Next/Get Started button
  const btnAnimWidth = React.useRef(new Animated.Value(BTN_WIDTH)).current;

  const navigation = useNavigation<NavigationProp<RootStackParamList>>();

  const insets = useSafeAreaInsets();

  // --- Improved button width animation (animates both ways) ---
  const isGetStartedSlide = currentIndex === slides.length - 2;
  React.useEffect(() => {
    let toValue = BTN_WIDTH;
    if (isGetStartedSlide) {
      toValue = BTN_WIDE_WIDTH;
    }
    Animated.timing(btnAnimWidth, {
      toValue,
      duration: 350,
      useNativeDriver: false,
    }).start();
    setLoading(false);
  }, [currentIndex, isGetStartedSlide]);
  // ------------------------------------------------------------

  const getUserID = async (): Promise<string | null> => {
          try {
            const session = await fetchAuthSession();
            const user_id = session?.tokens?.accessToken?.payload?.sub;
            return user_id || null;
          } catch (error) {
            console.error('Error retrieving user ID:', error);
            return null;
          }
        };
      
  const getAuthToken = useCallback(async (): Promise<any | null> => {
    try {
      const session = await fetchAuthSession();
      const id_token = session?.tokens?.idToken;
      return id_token || null;
    } catch (error) {
      console.error('Error retrieving auth token:', error);
      return null;
    }
  }, []);

  const updateSettings = async () => {
    try {
      const user_id = await getUserID();
      const authToken = await getAuthToken();
      const response = await fetch(`${apiUrl}settings/set-user-settings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({
          user_id,
          'completed_tutorial': true,
        }),
      });
      console.log('Settings updated successfully:', await response.json());
    } catch (error) {
      console.error('Error updating settings:', error);
    }
  };

  const getDotWidth = (i: number) =>
    scrollX.interpolate({
      inputRange: [
        (i - 1) * width,
        i * width,
        (i + 1) * width
      ],
      outputRange: [DOT_SIZE, DASH_WIDTH, DOT_SIZE],
      extrapolate: 'clamp'
    });

  const handleNext = () => {
  if (currentIndex < slides.length - 1 && scrollRef.current) {
    (scrollRef.current as any).scrollTo({ x: (currentIndex + 1) * width, animated: true });
  }
};

const handleDotPress = (index: number) => {
  if (scrollRef.current) {
    (scrollRef.current as any).scrollTo({ x: index * width, animated: true });
    setCurrentIndex(index); // If you manually manage currentIndex, keep this
  }
};


  // Only show Next button for feature slides (1-3)
  const isFeatureSlideIdx = currentIndex > 0 && currentIndex < slides.length - 2;
  const isPlanSlideIdx = currentIndex === slides.length - 1;


  // Handler for "Get Started" button on last feature slide
  const handleGetStarted = () => {
    if (scrollRef.current) {
      (scrollRef.current as any).scrollTo({ x: (currentIndex + 1) * width, animated: true });
    }
  };

  // Handlers for hub actions
  const handleTryFree = () => setShowFreeTokens(true);
  const handleViewSubscriptions = async () => {
    await updateSettings();

    if (Platform.OS === 'android') {
      setGateIntent('subscription');
      setShowParentalGate(true);
    } else {
      navigation.navigate('SubscriptionPage');
    }
  };
  const handleParentalGateSuccess = () => {
    if (gateIntent === 'subscription') {
      navigation.navigate('SubscriptionPage');
    }
    setShowParentalGate(false);
    setGateIntent(null);
  };

  const handleParentalGateCancel = () => {
    setShowParentalGate(false);
    setGateIntent(null);
  };
  const handleChooseStory = async () => {
    await updateSettings()
    navigation.navigate('StoriesPage');
  };
  const handleBackFromTokens = () => setShowFreeTokens(false);

  const STATUSBAR_HEIGHT = Platform.OS === 'ios' ? 44 : StatusBar.currentHeight || 24;

  const StatusBarBackground = ({ backgroundColor = '#F7F8FB' }) => (
    Platform.OS === 'ios' ? (
      <View style={{
        height: STATUSBAR_HEIGHT,
        width: '100%',
        position: 'absolute',
        top: 0,
        left: 0,
        backgroundColor,
        zIndex: 1000,
      }}/>
    ) : null
  );

  return (
    <GestureHandlerRootView style={{ flex: 1, backgroundColor: '#F7F8FB' }}>
    <SafeAreaView style={{ flex: 1, backgroundColor: '#F7F8FB' }} edges={['top', 'bottom']}>
      {Platform.OS === 'ios' && (
        <View style={{ height: insets.top, backgroundColor: '#F7F8FB', width: '100%' }} />
      )}
      <StatusBar barStyle="dark-content" />
      <View style={styles.centeredContent}>
        <Animated.ScrollView
          ref={scrollRef}
          horizontal
          pagingEnabled
          showsHorizontalScrollIndicator={false}
          scrollEnabled={!showFreeTokens}
          style={styles.container}
          {...(Platform.OS === 'web' ? { className: 'no-horizontal-scrollbar' } : {})}
          onScroll={Animated.event(
          [{ nativeEvent: { contentOffset: { x: scrollX } } }],
          {
            useNativeDriver: false,
            listener: (e: NativeSyntheticEvent<NativeScrollEvent>) => {
              const slide = Math.round(e.nativeEvent.contentOffset.x / width);
              setCurrentIndex(slide);
              // any other logic...
            },
          }
        )}
          scrollEventThrottle={16}
        >
          {/* Intro Slide */}
          <View style={{ width, flex: 1 }}>
            <IntroSlide />
          </View>
          {/* Feature and Plan Slides */}
          {slides.slice(1).map((slide, idx) => (
          <View style={{ width, flex: 1 }} key={idx + 1}>
            {isPlanSlide(slide) ? (
              !showFreeTokens ? (
                <PlanSelectorHub onTryFree={handleTryFree} onViewSubscriptions={handleViewSubscriptions} />
              ) : (
                <FreeTokensSlide onChooseStory={handleChooseStory} onBack={handleBackFromTokens} />
              )
            ) : isFeatureSlide(slide) ? (
              <AnimatedSplitSlide
                title={slide.title}
                top={slide.top}
                bottom={slide.bottom}
                isActive
              />
            ) : (
              <View style={styles.splitSlide} />
            )}
          </View>
        ))}

        </Animated.ScrollView>

        {/* Expanding dots/dashes */}
        <View style={styles.dotsContainer}>
        {Array.from({ length: NUM_SLIDES }).map((_, i) => {
          const backgroundColor = scrollX.interpolate({
            inputRange: [
              (i - 1) * width,
              i * width,
              (i + 1) * width
            ],
            outputRange: ['#8690A7', '#544564', '#8690A7'],
            extrapolate: 'clamp'
          });
          return (
            <TouchableOpacity key={i} onPress={() => handleDotPress(i)} activeOpacity={0.6}>
              <Animated.View
                style={[
                  styles.dot,
                  {
                    width: getDotWidth(i),
                    marginRight: i !== NUM_SLIDES - 1 ? DOT_SPACING : 0,
                    backgroundColor,
                  }
                ]}
              />
            </TouchableOpacity>
          );
        })}
      </View>


        {/* Next / Get Started button */}
        {currentIndex === 0 && (
          <Animated.View style={[styles.animatedBtnWrap, { width: btnAnimWidth }]}>
            <TouchableOpacity style={styles.nextBtn} onPress={handleNext} activeOpacity={0.85}>
              <Text style={styles.nextBtnText}>Next</Text>
            </TouchableOpacity>
          </Animated.View>
        )}
        {isFeatureSlideIdx && (
          <Animated.View style={[styles.animatedBtnWrap, { width: btnAnimWidth }]}>
            <TouchableOpacity style={styles.nextBtn} onPress={handleNext} activeOpacity={0.85}>
              <Text style={styles.nextBtnText}>Next</Text>
            </TouchableOpacity>
          </Animated.View>
        )}
        {isGetStartedSlide && (
          <Animated.View style={[styles.animatedBtnWrap, { width: btnAnimWidth }]}>
            <TouchableOpacity style={styles.nextBtn} onPress={handleGetStarted} activeOpacity={0.85}>
              <Text style={styles.nextBtnText}>Get Started</Text>
            </TouchableOpacity>
          </Animated.View>
        )}
      </View>
    </SafeAreaView>
    <ParentalGateModal
      isVisible={showParentalGate}
      onSuccess={handleParentalGateSuccess}
      onCancel={handleParentalGateCancel}
    />
    </GestureHandlerRootView>
  );
}

const styles = StyleSheet.create({
  container: { 
    flex: 1,
    width: width,
    height: height,
    backgroundColor: "#F7F8FB",
  },
  safeArea: {
    flex: 1, // This should be retained for proper layout
    backgroundColor: '#F7F8FB',
    overflow: 'hidden', // Prevent unwanted overflow behaviors
  },
  centeredContent: {
    flex: 1,
    width: width,
    height: height,
    alignSelf: 'center',
    maxWidth: scale(800),
  },
  introSlide: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    overflow: 'hidden',
    maxWidth: 800, 
    backgroundColor: "#F7F8FB",
  },
  pairedImg: {
    borderRadius: 24,
    borderWidth: 2,
    borderColor: '#FDFED1',
    backgroundColor: '#FDFED1',
  },
  centerTextBox: {
    alignItems: 'center',
    justifyContent: 'center',
    marginVertical: 10,
    width: '90%',
    borderRadius: 18,
    padding: 18,
  },
  welcomeSmall: {
    fontSize: scale(16),
    color: '#544564',
    textAlign: 'center',
    marginBottom: 18,
    fontWeight: '400',
  },
  welcomeLarge: {
    fontSize: scale(26),
    fontWeight: 'bold',
    color: '#160014',
    textAlign: 'center',
    lineHeight: scale(32),
  },
  splitSlide: {
    flex: 1,
    alignItems: 'center',
    paddingTop: verticalScale(70),   // Room for title above
    paddingBottom: verticalScale(36),
    justifyContent: 'center',
    maxWidth: 800,
    position: 'relative',            // Needed for absolute child positioning
    backgroundColor: "#F7F8FB",
  },
  splitTitle: {
    fontSize: scale(22),
    fontWeight: 'bold',
    color: '#160014',
    textAlign: 'center',
    marginTop: 0,
    marginBottom: 18,
    lineHeight: scale(28),
  },
  splitRowTop: {
    width: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: verticalScale(30),         // More space above (was 6)
    marginBottom: verticalScale(42),      // More space below (was 12)
    paddingHorizontal: scale(15),         // Slightly more side padding (was 11)
  },
  splitRowBottom: {
    width: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: verticalScale(8),          // Added, for space between rows (was 0)
    marginBottom: verticalScale(12),      // More space below (was 6)
    paddingHorizontal: scale(15),
  },
  splitTextBox: {
    paddingVertical: verticalScale(28),
    paddingHorizontal: scale(8),
    borderRadius: scale(8),
    marginVertical: verticalScale(1),
  },
  splitSubtitle: {
    fontSize: scale(16),
    fontWeight: 'bold',
    color: '#544564',
    marginBottom: 7,
    lineHeight: scale(21),
  },
  splitBody: {
    fontSize: scale(13),
    color: '#160014',
    fontWeight: '500',
    lineHeight: scale(18),
    marginTop: 2,
  },
  splitImg: {
    width: scale(145),           // shrinks on small screens
    height: verticalScale(145),  // also shrinks vertically
    borderRadius: scale(12),
    marginHorizontal: scale(6),
    backgroundColor: '#FDFED1',
    borderWidth: 0,
    borderColor: '#FDFED1',
  },
  dotsContainer: {
    position: 'absolute',
    left: 24,
    bottom: 64,
    flexDirection: 'row',
    alignItems: 'center',
    height: DOT_SIZE,
    width: DOT_TRACK_WIDTH,
  },
  dot: {
    height: DOT_SIZE,
    borderRadius: DOT_SIZE / 2,
     ...(Platform.OS === 'web' ? { cursor: 'pointer' } : {}),
  },
  animatedBtnWrap: {
    position: 'absolute',
    right: 32,
    bottom: 56,
    zIndex: 99,
    overflow: 'hidden',
    borderRadius: 20,
  },
  nextBtn: {
    backgroundColor: '#544564',
    borderRadius: scale(15),
    paddingVertical: verticalScale(7),
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
  },
  nextBtnText: {
    color: '#FFFFFF',
    fontSize: scale(14),
    fontWeight: 'bold',
    letterSpacing: 1,
    textAlign: 'center',
  },
  // Paywall hub buttons
  bigPrimaryBtn: {
    backgroundColor: '#544564',
    borderRadius: 20,
    paddingVertical: 18,
    paddingHorizontal: 20,
    marginVertical: 14,
    width: '100%',
    alignItems: 'center',
  },
  bigPrimaryBtnText: {
    color: '#FFF',
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 4,
    textAlign: 'center',
  },
  planFreeDesc: {
    color: '#FDFED1',
    fontSize: 15,
    textAlign: 'center',
  },
  bigSecondaryBtn: {
    backgroundColor: '#F7F2EC',
    borderRadius: 20,
    borderWidth: 1,
    borderColor: '#544564',
    paddingVertical: 16,
    paddingHorizontal: 20,
    marginVertical: 6,
    width: '100%',
    alignItems: 'center',
  },
  bigSecondaryBtnText: {
    color: '#544564',
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 3,
    textAlign: 'center',
  },
  planSubDesc: {
    color: '#544564',
    fontSize: 15,
    textAlign: 'center',
  },
  // Free tokens slide styles
  tokensSlide: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 24,
    maxWidth: 800,
  },
  tokensImg: {
    width: 120,
    height: 120,
    marginBottom: 16,
  },
  tokensTitle: {
    fontSize: 26,
    fontWeight: 'bold',
    color: '#160014',
    textAlign: 'center',
    marginBottom: 8,
  },
  tokensSubtitle: {
    fontSize: 17,
    color: '#544564',
    textAlign: 'center',
    marginBottom: 22,
  },
  tokensCtaBtn: {
    backgroundColor: '#544564',
    borderRadius: 20,
    paddingVertical: 13,
    paddingHorizontal: 40,
    alignSelf: 'center',
  },
  tokensCtaText: {
    color: '#FFF',
    fontSize: 17,
    fontWeight: 'bold',
    textAlign: 'center',
  },
  planSlide: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingTop: 24,
    paddingHorizontal: 24,
    maxWidth: 800,
  },
  planHeroImg: {
    width: 120,
    height: 120,
    borderRadius: 14,
    borderWidth: 1,
    marginBottom: 14,
  },
  planTitle: {
    fontSize: 26,
    fontWeight: 'bold',
    color: '#160014',
    textAlign: 'center',
    marginVertical: 8,
  },
  planBullet: {
    fontSize: 16,
    color: '#544564',
    marginBottom: 10,
    textAlign: 'center',
  },
});